// src/features/engine/tasks/osrboi/components/OSRBOIGraph.js

import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { variableTranslations } from '../../../../../constants/variableTranslations';
import useOSRBOITranslator from '../hooks/useOSRBOITranslator';

export function getOSRBOILegendLabels(t) {
  return [
    t('your_evaluation'),
    t('quebec_reference_observations')
  ];
}

const OSRBOIGraph = ({ attributeName, userValues = [], sharedValues = [] }) => {
  const t = useOSRBOITranslator();
  const d3Container = useRef(null);
  const [containerWidth, setContainerWidth] = useState(800);

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      if (entries[0]?.contentRect?.width) {
        setContainerWidth(entries[0].contentRect.width);
      }
    });

    if (d3Container.current?.parentElement) {
      observer.observe(d3Container.current.parentElement);
    }

    return () => observer.disconnect();
  }, []);

  const getJitter = (range = 1) => (Math.random() - 0.5) * range;

  useEffect(() => {
    if (!d3Container.current) return;

    const margin = { left: 40, right: 120, top: 40, bottom: 40 };
    const width = containerWidth - margin.left - margin.right;
    const height = 70;

    const svg = d3.select(d3Container.current);
    svg.selectAll('*').remove();

    svg
      .attr('viewBox', `0 0 ${containerWidth} ${height + margin.top + margin.bottom}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('overflow', 'visible');

    const chart = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const xScale = d3.scaleLinear().domain([0, 100]).range([0, width]);

    // 🔹 Label atribut
    const displayName = variableTranslations[attributeName]
      ? t(variableTranslations[attributeName])
      : attributeName;

    svg.append("text")
      .attr("x", margin.left)
      .attr("y", margin.top / 2)
      .text(displayName)
      .style("font-size", "14px")
      .style("font-weight", "bold")
      .style("fill", "#333");

    // 🔹 Eix
    chart.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale).ticks(5).tickFormat(d => `${d}`))
      .selectAll("text")
      .style("font-size", "12px")
      .style("fill", "#333");

    // ============================================================
    // 🧠 POSICIONS Y (CLAU DEL FIX)
    // ============================================================

    const baseY = height - 10;
    const userY = baseY;        // 🔴 usuari (a prop de l’eix)
    const sharedY = baseY - 12; // 🟢 referència (una mica més amunt)

    // ============================================================
    // 🟢 SHARED (BACKGROUND)
    // ============================================================

    const sharedLayer = chart.append("g");

    sharedValues.forEach(({ value }) => {
      if (typeof value === 'number') {
        sharedLayer.append("circle")
          .attr("cx", xScale(value))
          .attr("cy", sharedY + getJitter(6))
          .attr("r", 5)
          .style("fill", "#209385")
          .style("stroke", "#fff")
          .style("stroke-width", 1);
      }
    });

    // ============================================================
    // 🔴 USER (FOREGROUND)
    // ============================================================

    const userLayer = chart.append("g");

    userValues.forEach(({ value }) => {
      if (typeof value === 'number') {
        userLayer.append("circle")
          .attr("cx", xScale(value))
          .attr("cy", userY + getJitter(6))
          .attr("r", 6)
          .style("fill", "red")
          .style("stroke", "#fff")
          .style("stroke-width", 2);
      }
    });

    // ============================================================
    // 🔸 LLEGENDA
    // ============================================================

    const legend = svg.append('g')
      .attr('transform', `translate(${containerWidth - 250}, 10)`);

    const [userLabel, referenceLabel] = getOSRBOILegendLabels(t);
    const legendData = [
      { color: "red", fill: "red", label: userLabel },
      { color: "#209385", fill: "#209385", label: referenceLabel }
    ];

    legend.selectAll("circle")
      .data(legendData)
      .enter()
      .append("circle")
      .attr("cx", 6)
      .attr("cy", (_, i) => i * 20 + 6)
      .attr("r", 5)
      .style("fill", d => d.fill)
      .style("stroke", d => d.color)
      .style("stroke-width", 2);

    legend.selectAll("text")
      .data(legendData)
      .enter()
      .append("text")
      .attr("x", 20)
      .attr("y", (_, i) => i * 20 + 10)
      .text(d => d.label)
      .style("font-size", "12px");

  }, [attributeName, userValues, sharedValues, containerWidth, t]);

  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      <svg ref={d3Container} width="100%" height="150px"></svg>
    </div>
  );
};

export default OSRBOIGraph;
